import { HelperActionRows } from '@/components/aiTools/HelperActionRows';
/**
 * AssessmentDrafter — Initial Assessment Drafter tool page (Phase X.3.5 →
 * 2026-07 gap-interview redesign → three-act staging).
 *
 * Three acts (structure tier of the redesign):
 *   PRE-GENERATION — one centered column: client block, notes, generate.
 *     The library and the finalize apparatus don't render.
 *   POST-GENERATION — the input form collapses to a summary chip ("Your
 *     notes · 1,240 words · View / edit"); the draft takes the stage at
 *     full width with a sticky section outline carrying per-section gap
 *     counts. The recommendations library became an INSERT action inside
 *     the draft's Recommendations section.
 *   FINALIZE — the explicit terminal ladder: the editable billing
 *     suggestion (a suggestion only; case notes are the only road onto
 *     an invoice), Save draft (secondary), then Download Word (primary,
 *     enabled by save, destination named in copy).
 *
 * Trust architecture (PR 1): anchored gap flags with per-flag answers +
 * the targeted patch pass; paragraph states (edited persists / changed
 * clears when viewed); the one-line run summary proving the merge
 * promise. The ONE client block feeds the form, the pronoun resolution,
 * and the library.
 *
 * Pronouns: a picked client's recorded pronouns fill the select, and a
 * bound client needs a choice before Generate and before the library
 * loads. After generation the live select is the one source: Save, the
 * change request and every added recommendation follow it, while the
 * gap-patch keeps the pronouns the report prose was written with. When the
 * two differ, a line names the set the report was written with.
 */

import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useOwnCaseloadPicks } from '@/lib/cmHomeApi';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { useTenant } from '@/lib/auth';
import { isSpiceOnlyViewer } from '@/lib/spiceOnlyViewer';
import {
  canAuthorClinicalNotes,
  canUseAiTools,
  canReadEveryClient,
  canViewAnyClientRoster,
} from '@/lib/capabilities';
import { noteDraftsApi } from '@/lib/noteDrafts';
import { noteDateDefault } from '@/lib/practiceDate';
import { useAiDraft } from '@/lib/aiDrafts';
import { assessmentDraftFromWire } from '@/lib/assessmentDraftResume';
import { useReresolvedPicks, type RecommendationSource } from '@/lib/recommendationText';
import { assembleAssessmentText } from '@/lib/assessmentText';
import { DownloadDocxButton } from '@/components/DownloadDocxButton';
import { DocumentPrintPdfButton, PRINT_PDF_LABEL } from '@/components/DocumentPrintPdfButton';
import { SaveToClientDocumentsButton } from '@/components/SaveToClientDocumentsButton';
import { ToolPageHeader } from '@/components/aiTools/ToolPageHeader';
import { ToolDraftingState } from '@/components/aiTools/ToolPageShell';
import { EmptyAiToolsTile } from '@/components/aiTools/EmptyAiToolsTile';
import { AssessmentDraftHelp } from '@/components/aiTools/AssessmentDraftHelp';
import { AssessmentDraftForm, type AssessmentFormFields } from '@/components/aiTools/AssessmentDraftForm';
import {
  AssessmentDraftOutput,
  type AssembledRecommendation,
  type ParagraphState,
} from '@/components/aiTools/AssessmentDraftOutput';
import {
  AssessmentClientBlock,
  boundClient,
  buildPronounChoice,
  EMPTY_PRONOUN_CUSTOM,
  pronounChoiceKey,
  pronounChoiceLabel,
  pronounCustomComplete,
  pronounsNeeded,
  type PronounCustomForms,
  type PronounPresetChoice,
  type PronounSource,
} from '@/components/aiTools/AssessmentClientBlock';
import { AssessmentGapInterview } from '@/components/aiTools/AssessmentGapInterview';
import { AssessmentSectionOutline } from '@/components/aiTools/AssessmentSectionOutline';
import { AssessmentBillingField } from '@/components/aiTools/AssessmentBillingField';
import { AssessmentRecommendationInsert } from '@/components/aiTools/AssessmentRecommendationInsert';
import { SpiceAssessmentOutput } from '@/components/aiTools/SpiceAssessmentOutput';
import { RefinePanel } from '@/components/aiTools/RefinePanel';
import { type EmailMode } from '@/components/aiTools/EmailModeToggle';
import { Button } from '@/components/primitives/Button';
import {
  aiRefineKeyOf,
  aiSaveKeyOf,
  assessmentFileKeyOf,
  errorText,
  type KeyedError,
} from '@/lib/errorCopy';
import { ErrorNotice } from '@/components/ErrorNotice';
import { ErrorBanner } from '@/components/ErrorBanner';
import { useSupportContact } from '@/hooks/useSupportContact';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { readAiInputMaxChars } from '@/lib/aiInputLimits';
import { aiToolFailureCopy, aiToolFailureOf, type AiToolFailure } from '@/lib/aiTextTooLong';
import { useInvalidateOnCeilingRefusal } from '@/hooks/useInvalidateOnCeilingRefusal';
import {
  aiToolsApi,
  type AssessmentBillingSuggestion,
  type AssessmentDraftResponse,
  type AssessmentFormInputSnapshot,
  type AssessmentGapAnswer,
  type AssessmentMode,
  type SpiceAssessmentDraftResponse,
  type SpicePronounChoice,
  type UpdateAssessmentDraftInput,
  ApiError,
} from '@/lib/api';
import {
  diffAdlPaths,
  diffLeafPaths,
  gapCountsBySection,
  runChangeSummary,
} from '@/lib/assessmentSections';
import { AI_TOOLS } from './AiTools';
import { KeptWorkNotice } from '@/components/KeptWorkNotice';
import { keptWorkKey, useKeptWork } from '@/lib/keptWork';

const TOOL = AI_TOOLS.find((t) => t.slug === 'assessment')!;

const CARD_CLS = 'rounded-card bg-paper p-6 shadow-sh2';
const EYEBROW_CLS = 'text-[0.71875rem] font-semibold uppercase tracking-[0.16em]';

function wordCount(text: string): number {
  const t = text.trim();
  return t.length === 0 ? 0 : t.split(/\s+/).length;
}

const THEY_THEM: SpicePronounChoice = { preset: 'they_them' };

const LIBRARY_PRONOUN_PROMPT =
  "Open View / edit above, then choose the client's pronouns in the client block to load the library.";

/** The form input with its pronoun choice replaced; no choice leaves no key. */
function withPronouns(
  formInput: AssessmentFormInputSnapshot,
  pronouns: SpicePronounChoice | undefined,
): AssessmentFormInputSnapshot {
  const next: AssessmentFormInputSnapshot = { ...formInput };
  delete next.pronouns;
  delete next.report_pronouns;
  if (pronouns !== undefined) next.pronouns = pronouns;
  return next;
}

/** What one save of the drafter carries, read at the press. */
interface SaveVars {
  content: Parameters<typeof aiToolsApi.updateAssessmentDraft>[2];
  reopenedDraftId: string | null;
  aiInvocationId: Parameters<typeof aiToolsApi.saveAssessmentDraft>[1]['ai_invocation_id'];
  clientId: string | null;
}

/** What a reload or an expired sign-in keeps of this page: the whole
 *  form, and the report once written (src/lib/keptWork.ts). */
interface KeptAssessment {
  fields: AssessmentFormFields;
  pickedMode: EmailMode;
  clientFirstName: string;
  clientLastName: string;
  recipient: string;
  pronounPreset: PronounPresetChoice;
  pronounCustom: PronounCustomForms;
  pronounSource: PronounSource | null;
  pickedClient: { id: string; firstName: string; lastName: string } | null;
  recommendedResources: AssembledRecommendation[];
  report: {
    response: AssessmentDraftResponse | SpiceAssessmentDraftResponse;
    formInput: AssessmentFormInputSnapshot;
    mode: AssessmentMode;
    proseChoice: SpicePronounChoice | null;
    paragraphStates: Record<string, ParagraphState>;
    reopenedDraftId: string | null;
    savedClientId: string | null;
  } | null;
}

export function AssessmentDrafter(): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  // X.6.4B: mode is lifted here so the header chip + output panel react to
  // SPICE. A SPICE-only viewer (does SPICE, not care management) has no
  // Mode toggle: the page is its SPICE face whatever was picked, read
  // from the viewer on every render so a whoami that arrives late still
  // lands her there.
  const spiceOnlyViewer = isSpiceOnlyViewer(whoami);
  const [pickedMode, setMode] = useState<EmailMode>('care_management');
  const mode: EmailMode = spiceOnlyViewer ? 'spice' : pickedMode;
  const [isGenerating, setIsGenerating] = useState(false);
  const [draftResponse, setDraftResponse] = useState<
    AssessmentDraftResponse | SpiceAssessmentDraftResponse | null
  >(null);
  const [lastFormInput, setLastFormInput] = useState<AssessmentFormInputSnapshot | null>(null);
  const [lastMode, setLastMode] = useState<AssessmentMode>('care_management');
  // WS-9: failures are stored as registry keys (errorCopy.ts owns the
  // copy); null = no error showing.
  const [saveError, setSaveError] = useState<KeyedError | null>(null);
  const [savedDraftId, setSavedDraftId] = useState<string | null>(null);
  // The saved draft's client: where Save to client documents files it.
  const [savedClientId, setSavedClientId] = useState<string | null>(null);
  // A saved report reopened from the drafts inbox (?draft=<id>): Save
  // writes back to this row instead of inserting a new one.
  const [reopenedDraftId, setReopenedDraftId] = useState<string | null>(null);
  // Post-generation: the input collapses to a summary chip; View / edit
  // re-expands it (e.g. to adjust the notes and regenerate).
  const [inputExpanded, setInputExpanded] = useState(false);

  // The ONE client block (founder ruling): the whole page reads from it.
  const [clientFirstName, setClientFirstName] = useState('');
  const [clientLastName, setClientLastName] = useState('');
  const [recipient, setRecipient] = useState('');
  const [pronounPreset, setPronounPreset] = useState<PronounPresetChoice>('');
  const [pronounCustom, setPronounCustom] = useState<PronounCustomForms>({
    ...EMPTY_PRONOUN_CUSTOM,
  });
  // Where the select's value came from; null whenever the select is ''.
  const [pronounSource, setPronounSource] = useState<PronounSource | null>(null);
  const pronounChoice = buildPronounChoice(pronounPreset, pronounCustom);
  const pronounsComplete = pronounCustomComplete(pronounPreset, pronounCustom);
  // The pronoun choice the report prose was last written with: set by a
  // generation, a change request, and a reopen; a gap-patch never moves it.
  const [proseChoice, setProseChoice] = useState<SpicePronounChoice | undefined>(undefined);
  // A reopened draft that recorded no choice: the library asks for one.
  // Generate never waits on it; the next generation clears it.
  const [reopenedWithoutPronouns, setReopenedWithoutPronouns] = useState(false);

  // A lookup-picked client (id + the name at pick time). Filing the finished
  // assessment to a record needs a real client_id; free-text entry has none.
  // The name is captured so an after-the-fact manual edit disables filing
  // (the on-screen subject would no longer be the picked client).
  const [pickedClient, setPickedClient] = useState<{
    id: string;
    firstName: string;
    lastName: string;
  } | null>(null);
  const bound = boundClient(pickedClient, clientFirstName, clientLastName);
  // A bound client needs a choice before Generate; the library also waits
  // for one after a reopen that recorded none.
  const pronounsRequired = bound !== null;
  const libraryNeedsPronouns = pronounsNeeded(
    pronounPreset,
    pronounCustom,
    bound !== null || reopenedWithoutPronouns,
  );
  // The file-to-record result: the destination first name + whether the click
  // replayed an identical, already-filed assessment (idempotent double-file).
  const [fileResult, setFileResult] = useState<{ firstName: string; alreadyFiled: boolean } | null>(
    null,
  );
  const [fileError, setFileError] = useState<KeyedError | null>(null);

  // v2 click-to-insert: vetted recommendations the CM added to the report.
  const [recommendedResources, setRecommendedResources] = useState<AssembledRecommendation[]>([]);
  const recIdRef = useRef(0);
  // Founder polish (2026-07-06, item 4): the insert picker's open state is
  // page-owned so the sidebar outline entry opens the SAME picker.
  const [recPickerOpen, setRecPickerOpen] = useState(false);

  // Paragraph trust states, keyed by leaf path: 'edited' persists;
  // 'changed' (by the last run) clears when the paragraph is viewed.
  const [paragraphStates, setParagraphStates] = useState<Record<string, ParagraphState>>({});
  // The merge-promise proof line after any re-run.
  const [lastRunSummary, setLastRunSummary] = useState<string | null>(null);

  // Second-round edits: the report as it read BEFORE the last run,
  // restorable until the next generation/run (client-side only).
  const [previousResponse, setPreviousResponse] = useState<AssessmentDraftResponse | null>(null);
  // The last failure of a revision and of a fill-in, each a registry key or
  // a refusal for length with the box it names; null = no error showing.
  const [refineFailure, setRefineFailure] = useState<AiToolFailure | null>(null);
  const [gapFailure, setGapFailure] = useState<AiToolFailure | null>(null);
  const responseBeforeRefineRef = useRef<AssessmentDraftResponse | null>(null);
  const supportContact = useSupportContact();
  // The Want changes? box is the email helper's panel and takes its input
  // ceiling, from the tenant config; the factory value until it has loaded.
  const tenantConfig = useTenantConfig(tenantSlug).data?.config;
  const emailMaxChars = readAiInputMaxChars(tenantConfig, 'email');
  const onCeilingRefusal = useInvalidateOnCeilingRefusal(tenantSlug);
  // A notice's sentence, built at render: a refusal for length says the
  // server's number, else the named box's ceiling as the config reads now
  // (the carried notes have their own, `assessment_notes`).
  const refineErrorText = (f: AiToolFailure): string => {
    const copy = aiToolFailureCopy(f, (entry) => readAiInputMaxChars(tenantConfig, entry));
    return errorText('assessment_refine', copy.key, supportContact, copy.vars);
  };
  const queryClient = useQueryClient();

  // The pronoun choice in effect: the select, or they/them when nothing is
  // chosen and nothing needs a choice. SPICE has no default.
  const effectiveChoice: SpicePronounChoice | undefined =
    lastMode === 'spice'
      ? pronounChoice
      : (pronounChoice ?? (libraryNeedsPronouns ? undefined : THEY_THEM));
  const pronounMismatch =
    proseChoice !== undefined &&
    effectiveChoice !== undefined &&
    pronounChoiceKey(proseChoice) !== pronounChoiceKey(effectiveChoice);

  // Inserted recommendations follow the choice in effect; any re-wording
  // makes a prior save stale.
  const {
    picksPending,
    currentKey: currentPronounKey,
    retry: retryRecommendation,
  } = useReresolvedPicks({
    tenantSlug,
    firstName: clientFirstName,
    effectiveChoice,
    picks: recommendedResources,
    setPicks: setRecommendedResources,
    onRewrite: () => setSavedDraftId(null),
  });

  // Every change the care manager makes to the select or the custom forms
  // (a pick and an unbind included) counts as an edit. After generation an
  // edit records the choice then in effect as the draft's choice, and makes
  // a prior save stale.
  const [pronounEdits, setPronounEdits] = useState(0);
  const handlePronounPresetChange = (v: PronounPresetChoice, source: PronounSource): void => {
    setPronounPreset(v);
    setPronounSource(v === '' ? null : source);
    setPronounEdits((n) => n + 1);
  };
  const handlePronounCustomChange = (v: PronounCustomForms): void => {
    setPronounCustom(v);
    setPronounEdits((n) => n + 1);
  };
  useEffect(() => {
    if (pronounEdits === 0 || draftResponse === null) return;
    setLastFormInput((fi) => (fi === null ? fi : withPronouns(fi, effectiveChoice)));
    setSavedDraftId(null);
  }, [pronounEdits]);

  // Unbinding (a name edit after a pick) clears a choice that came from the
  // record, since the subject on screen is no longer that client. A care
  // manager's own choice stays.
  const boundId = bound?.id ?? null;
  const lastBoundIdRef = useRef<string | null>(null);
  useEffect(() => {
    const wasBound = lastBoundIdRef.current !== null;
    lastBoundIdRef.current = boundId;
    if (wasBound && boundId === null && pronounSource === 'record') {
      handlePronounPresetChange('', 'record');
      handlePronounCustomChange({ ...EMPTY_PRONOUN_CUSTOM });
    }
  }, [boundId]);

  // Reopen: ?draft=<id> loads the saved report once, then this page owns
  // the state (a background refetch never clobbers in-progress edits).
  const [searchParams] = useSearchParams();
  // A line seat (no all-clients read) picks the client to file to from her
  // own caseload, the same rows the email helper's filing door lists. Hooks
  // sit above the page's early returns.
  const canLookupAllClients = canReadEveryClient(whoami);
  const usesOwnCaseload = canAuthorClinicalNotes(whoami) && !canLookupAllClients;
  const ownCaseload = useOwnCaseloadPicks(tenantSlug, usesOwnCaseload);
  const resumeId = searchParams.get('draft');
  const hydratedRef = useRef<string | null>(null);
  const {
    draft: resumedDraft,
    isLoading: resumeLoading,
    isError: resumeError,
    error: resumeErr,
  } = useAiDraft(tenantSlug, resumeId ?? '', {
    enabled: canUseAiTools(whoami) && resumeId !== null && hydratedRef.current !== resumeId,
  });
  useEffect(() => {
    if (!resumedDraft || hydratedRef.current === resumedDraft.id) return;
    const resumed = assessmentDraftFromWire(resumedDraft);
    if (resumed === null) return;
    hydratedRef.current = resumedDraft.id;
    setDraftResponse(resumed.response);
    setLastFormInput(resumed.formInput);
    setLastMode(resumed.mode);
    setMode(resumed.mode);
    setClientFirstName(resumed.formInput.client_first_name);
    setClientLastName(resumed.formInput.client_last_name);
    setRecipient(resumed.formInput.recipient);
    // The saved choice returns to the select as the care manager's own; the
    // prose keeps the choice it was written with. Absent means no choice was
    // recorded, and the library asks for one.
    const savedChoice = resumed.formInput.pronouns;
    setPronounPreset(savedChoice?.preset ?? '');
    setPronounSource(savedChoice !== undefined ? 'cm' : null);
    setPronounCustom({ ...EMPTY_PRONOUN_CUSTOM, ...savedChoice?.custom });
    setProseChoice(resumed.formInput.report_pronouns ?? savedChoice);
    setReopenedWithoutPronouns(savedChoice === undefined);
    setRecommendedResources(
      resumed.recommendedResources.map((rec) => ({ id: `rec-${recIdRef.current++}`, ...rec })),
    );
    // The row is the saved state: Save reads Saved and the Word download is
    // live until an edit. Only a report still in draft writes back to its
    // row; a finalized one opened by URL saves as a new draft.
    setSavedDraftId(resumedDraft.id);
    setSavedClientId(resumedDraft.client_id ?? null);
    setReopenedDraftId(resumedDraft.status === 'draft' ? resumedDraft.id : null);
    setSaveError(null);
    setRecPickerOpen(false);
    setPreviousResponse(null);
    setRefineFailure(null);
    setGapFailure(null);
    setParagraphStates({});
    setLastRunSummary(null);
    setFileResult(null);
    setFileError(null);
    setInputExpanded(false);
  }, [resumedDraft]);

  // An expired sign-in keeps the generated report as edited, with the
  // client block it was written for (src/lib/keptWork.ts). Before a
  // generation the notes field is the work, and it keeps itself.
  // A reload or an expired sign-in keeps the whole form: the client block,
  // the pronouns, the picked recommendations, the mode, the form's own fields
  // (the notes among them) and, once written, the report as edited
  // (src/lib/keptWork.ts). Restoring brings all of it back together.
  const [formFields, setFormFields] = useState<AssessmentFormFields | null>(null);
  const [keptFields, setKeptFields] = useState<AssessmentFormFields | undefined>(undefined);
  // A restore remounts the form so its fields start from the kept copy.
  const [formGeneration, setFormGeneration] = useState(0);
  const kept = useKeptWork<KeptAssessment>(
    keptWorkKey(tenantSlug, 'assessment', resumeId),
    () => {
      if (savedDraftId !== null) return null;
      const fields = formFields ?? { notes: '', age: '', assessmentDate: '', location: '', attendees: '' };
      const typed =
        draftResponse !== null ||
        pickedClient !== null ||
        recommendedResources.length > 0 ||
        [clientFirstName, clientLastName, recipient, ...Object.values(fields)].some((v) => v.trim() !== '');
      if (!typed) return null;
      return {
        fields,
        pickedMode,
        clientFirstName,
        clientLastName,
        recipient,
        pronounPreset,
        pronounCustom,
        pronounSource,
        pickedClient,
        recommendedResources,
        report:
          draftResponse === null || lastFormInput === null
            ? null
            : {
                response: draftResponse,
                formInput: lastFormInput,
                mode: lastMode,
                proseChoice: proseChoice ?? null,
                paragraphStates,
                reopenedDraftId,
                savedClientId,
              },
      };
    },
    (k) => {
      // The kept copy is newer than the saved row a reopen would load.
      hydratedRef.current = resumeId;
      setKeptFields(k.fields);
      setFormGeneration((n) => n + 1);
      setMode(k.pickedMode);
      setClientFirstName(k.clientFirstName);
      setClientLastName(k.clientLastName);
      setRecipient(k.recipient);
      setPronounPreset(k.pronounPreset);
      setPronounCustom(k.pronounCustom);
      setPronounSource(k.pronounSource);
      setPickedClient(k.pickedClient);
      setRecommendedResources(k.recommendedResources);
      if (k.report !== null) {
        setDraftResponse(k.report.response);
        setLastFormInput(k.report.formInput);
        setLastMode(k.report.mode);
        setProseChoice(k.report.proseChoice ?? undefined);
        setParagraphStates(k.report.paragraphStates);
        setReopenedDraftId(k.report.reopenedDraftId);
        setSavedClientId(k.report.savedClientId);
        setInputExpanded(false);
      }
      setSavedDraftId(null);
    },
  );
  // Saved: the kept copy has done its job.
  useEffect(() => {
    if (savedDraftId !== null) kept.clear();
  }, [savedDraftId]);

  // Inline section edits (Care Management): the output component bubbles a
  // full next-response object; a prior save is now stale, so Save
  // re-enables and the (id-based) download requires a re-save.
  const handleResponseChange = (next: AssessmentDraftResponse): void => {
    setDraftResponse(next);
    setSavedDraftId(null);
    // An edit changes the report; a prior "filed" banner is now stale (a
    // re-file would create a fresh draft from the new content).
    setFileResult(null);
    setFileError(null);
  };

  const handleParagraphEdited = (path: string): void => {
    setParagraphStates((prev) => ({ ...prev, [path]: 'edited' }));
  };

  const handleParagraphSeen = (path: string): void => {
    setParagraphStates((prev) => {
      if (prev[path] !== 'changed') return prev;
      const next = { ...prev };
      delete next[path];
      return next;
    });
  };

  // Second-round runs share one mutation: the free-form change request
  // (RefinePanel) and the gap interview's "Fill these in" patch pass. Each
  // run carries its own pronoun choice (see handleRefine / handleFillIn).
  /** The refine request, built from the report and the form on screen at
   *  the press; null when there is no Care Management draft to refine. */
  function refineRequestOf(vars: {
    instruction: string;
    gap_answers?: AssessmentGapAnswer[];
    pronouns: SpicePronounChoice | undefined;
  }): Parameters<typeof aiToolsApi.refineAssessment>[1] | null {
    if (!draftResponse || !lastFormInput || lastMode !== 'care_management') return null;
    const r = draftResponse as AssessmentDraftResponse;
    return {
        instruction: vars.instruction,
        ...(vars.gap_answers ? { gap_answers: vars.gap_answers } : {}),
        current_report: {
          header: r.header,
          sections: r.sections,
          adl_iadl: r.adl_iadl,
          closing_courtesy: r.closing_courtesy,
          closing: r.closing,
          signature: r.signature,
          // Signature-integrity P0: server-owned unsigned marker rides along.
          signatory_missing: r.signatory_missing,
          follow_up_questions: r.follow_up_questions,
          billing_suggestion: r.billing_suggestion,
        },
        form_input: withPronouns(lastFormInput, vars.pronouns),
    };
  }

  // The request travels as the variables (refineRequestOf, at the press).
  const refineMutation = useMutation({
    mutationFn: (vars: {
      instruction: string;
      gap_answers?: AssessmentGapAnswer[];
      pronouns: SpicePronounChoice | undefined;
      request: Parameters<typeof aiToolsApi.refineAssessment>[1] | null;
    }) => {
      if (vars.request === null) throw new Error('refine called without a Care Management draft');
      return aiToolsApi.refineAssessment(tenantSlug, vars.request);
    },
    onSuccess: (res, vars) => {
      // A change request rewrote the prose with the choice it sent. A
      // gap-patch folds answers in the report's own pronouns, and a run sent
      // without a choice keeps them too.
      if (vars.gap_answers === undefined && vars.pronouns !== undefined) {
        setProseChoice(vars.pronouns);
      }
      const before = responseBeforeRefineRef.current;
      setPreviousResponse(before);
      setDraftResponse(res);
      setRefineFailure(null);
      setGapFailure(null);
      setSaveError(null);
      // The report changed; a prior save (and its download) is stale, and so
      // is any "filed to the record" banner.
      setSavedDraftId(null);
      setFileResult(null);
      setFileError(null);
      // Trust proof: mark exactly what the run touched, leave 'edited'
      // marks on everything it did not, and write the one-line summary.
      if (before !== null) {
        const changed = [
          ...diffLeafPaths(before.sections, res.sections),
          // The ADL/IADL rows are editable too; a run that touches one
          // marks it changed the same way (founder polish, item 3).
          ...diffAdlPaths(before.adl_iadl, res.adl_iadl),
        ];
        setParagraphStates((prev) => {
          const next = { ...prev };
          for (const p of changed) next[p] = 'changed';
          return next;
        });
        setLastRunSummary(runChangeSummary(changed));
      }
    },
    onError: (e: unknown, vars) => {
      onCeilingRefusal(e);
      // Both send the notes as they were at Generate. A fill-in sends no
      // instruction, so a refusal naming no box is about the carried notes;
      // on the panel it is about Want changes?.
      if (vars.gap_answers) {
        setGapFailure(aiToolFailureOf(e, 'assessment_refine', 'form_input.notes', vars.request, aiRefineKeyOf));
      } else {
        setRefineFailure(aiToolFailureOf(e, 'assessment_refine', 'instruction', vars.request, aiRefineKeyOf));
      }
    },
  });

  /** The save's variables, read at the press; null with no draft yet. */
  function saveVarsOf(): SaveVars | null {
    if (!draftResponse || !lastFormInput) return null;
    return {
      content: saveContent(draftResponse, lastFormInput),
      reopenedDraftId,
      aiInvocationId: draftResponse.ai_invocation_id,
      clientId: bound?.id ?? null,
    };
  }

  /** The record draft's text and hours, assembled at the press; a null
   *  body when there is no Care Management draft. */
  function fileVarsOf(): { body: string | null; hours: number | null } {
    const r = draftResponse as AssessmentDraftResponse | null;
    if (r === null || lastMode !== 'care_management') return { body: null, hours: null };
    return {
      body: assembleAssessmentText(
        r,
        recommendedResources.map((rec) => ({ topic: rec.topic, text: rec.text })),
      ),
      hours: r.billing_suggestion.hours,
    };
  }

  // A change request sends the choice in effect: the care manager may be
  // asking for the new pronouns.
  const handleRefine = (instruction: string): void => {
    responseBeforeRefineRef.current =
      lastMode === 'care_management' ? (draftResponse as AssessmentDraftResponse) : null;
    const vars = { instruction, pronouns: effectiveChoice };
    refineMutation.mutate({ ...vars, request: refineRequestOf(vars) });
  };

  // A gap-patch sends the choice the prose was written with, so the answers
  // read like the rest of the report.
  const handleFillIn = (answers: AssessmentGapAnswer[]): void => {
    responseBeforeRefineRef.current =
      lastMode === 'care_management' ? (draftResponse as AssessmentDraftResponse) : null;
    const vars = { instruction: '', gap_answers: answers, pronouns: proseChoice };
    refineMutation.mutate({ ...vars, request: refineRequestOf(vars) });
  };

  const handleRestorePrevious = (): void => {
    if (previousResponse === null) return;
    setDraftResponse(previousResponse);
    setPreviousResponse(null);
    setSavedDraftId(null);
    setLastRunSummary(null);
    // The restore rewinds the run's changes; drop the 'changed' marks.
    setParagraphStates((prev) => {
      const next: Record<string, ParagraphState> = {};
      for (const [k, v] of Object.entries(prev)) {
        if (v === 'edited') next[k] = v;
      }
      return next;
    });
  };

  /**
   * Insert a vetted recommendation (the section's insert action) with its
   * source, so it can be re-worded if the pronoun choice changes.
   * replaceTopic names an existing entry to swap out: an inserted rec is
   * replaced in place; a model-drafted recommendation is removed from
   * the report sections (a response change, so the save goes stale).
   */
  const handleInsertRecommendation = (
    text: string,
    topic: string,
    needsResources: boolean,
    source: RecommendationSource | undefined,
    replaceTopic?: string,
  ): void => {
    if (replaceTopic !== undefined) {
      const inserted = recommendedResources.find((r) => r.topic === replaceTopic);
      if (inserted !== undefined) {
        setRecommendedResources((prev) =>
          prev.map((r) =>
            r.id === inserted.id ? { id: r.id, topic, text, needsResources, ...source } : r,
          ),
        );
        setSavedDraftId(null);
        return;
      }
      const r = draftResponse as AssessmentDraftResponse | null;
      if (r !== null && lastMode === 'care_management') {
        const filtered = r.sections.summary_and_recommendations.recommendations.filter(
          (rec) => rec.label !== replaceTopic,
        );
        handleResponseChange({
          ...r,
          sections: {
            ...r.sections,
            summary_and_recommendations: {
              ...r.sections.summary_and_recommendations,
              recommendations: filtered,
            },
          },
        });
      }
    }
    setRecommendedResources((prev) => [
      ...prev,
      { id: `rec-${recIdRef.current++}`, topic, text, needsResources, ...source },
    ]);
    setSavedDraftId(null);
  };

  const handleRemoveRecommendation = (id: string): void => {
    setRecommendedResources((prev) => prev.filter((r) => r.id !== id));
    setSavedDraftId(null);
  };

  // The editable billing suggestion (terminal ladder): direct edits win
  // and persist with the save. Nothing is billed from here.
  const handleBillingChange = (next: AssessmentBillingSuggestion): void => {
    const r = draftResponse as AssessmentDraftResponse | null;
    if (r === null || lastMode !== 'care_management') return;
    handleResponseChange({ ...r, billing_suggestion: next });
  };

  // The report content the save routes take: each mode its own shape
  // (SPICE carries no billing). Nothing is billed from here. The form input
  // carries the draft's pronoun choice and the one the prose was written
  // with.
  const saveContent = (
    response: AssessmentDraftResponse | SpiceAssessmentDraftResponse,
    draftFormInput: AssessmentFormInputSnapshot,
  ): UpdateAssessmentDraftInput => {
    const formInput: AssessmentFormInputSnapshot = { ...draftFormInput };
    delete formInput.report_pronouns;
    if (proseChoice !== undefined) formInput.report_pronouns = proseChoice;
    if (lastMode === 'spice') {
      const r = response as SpiceAssessmentDraftResponse;
      return {
        mode: 'spice',
        header: r.header,
        salutation: r.salutation,
        opening: r.opening,
        sections: r.sections,
        skill_goals: r.skill_goals,
        modality_framing: r.modality_framing,
        modalities: r.modalities,
        summary: r.summary,
        sign_off: r.sign_off,
        // Signature-integrity P0: persist the unsigned marker.
        signatory_missing: r.signatory_missing,
        follow_up_questions: r.follow_up_questions,
        form_input: formInput,
      };
    }
    const r = response as AssessmentDraftResponse;
    return {
      header: r.header,
      sections: r.sections,
      adl_iadl: r.adl_iadl,
      // Item 7: round-trip the server-templated courtesy sentence.
      closing_courtesy: r.closing_courtesy,
      closing: r.closing,
      // The server-built e-signature block, persisted so the download
      // renders it line by line.
      signature: r.signature,
      // Signature-integrity P0: persist the unsigned marker.
      signatory_missing: r.signatory_missing,
      follow_up_questions: r.follow_up_questions,
      billing_suggestion: r.billing_suggestion,
      form_input: formInput,
      // v2: persist the CM-assembled recommendations (topic + body) so they
      // survive reload and flow into the .docx Recommendations region, each
      // with its topic as a branded subheading.
      recommended_resources: recommendedResources.map((rec) => ({
        topic: rec.topic,
        body: rec.text,
        needs_resources: rec.needsResources,
        ...(rec.ref !== undefined ? { ref: rec.ref } : {}),
        ...(rec.curation !== undefined ? { curation: rec.curation } : {}),
        ...(rec.worded_with !== undefined ? { worded_with: rec.worded_with } : {}),
      })),
    };
  };

  // A reopened draft writes back to its own row; a new report inserts one,
  // stamped with the generation it came from.
  // The draft, the row it updates and the client are read at the press
  // and travel as the variables (saveVarsOf).
  const saveMutation = useMutation({
    mutationFn: (v: SaveVars | null) => {
      if (v === null) throw new Error('saveDraft called without a generated draft');
      if (v.reopenedDraftId !== null) {
        return aiToolsApi.updateAssessmentDraft(tenantSlug, v.reopenedDraftId, v.content);
      }
      return aiToolsApi.saveAssessmentDraft(tenantSlug, {
        ai_invocation_id: v.aiInvocationId,
        client_id: v.clientId,
        ...v.content,
      });
    },
    onSuccess: (res, v) => {
      setSaveError(null);
      setSavedDraftId(res.id);
      // An update keeps the client its row was saved with.
      if (v !== null && v.reopenedDraftId === null) setSavedClientId(v.clientId);
      // The inbox preview and the reopen read now lag the row; the reopen
      // read is dropped so a later visit starts from the row, not a cache.
      void queryClient.invalidateQueries({ queryKey: ['ai-tools', 'drafts', tenantSlug] });
      queryClient.removeQueries({ queryKey: ['ai-tools', 'draft', tenantSlug, res.id] });
    },
    onError: (e: unknown) => {
      setSavedDraftId(null);
      setSaveError(aiSaveKeyOf(e));
    },
  });

  // File the finished assessment to the picked client's record as a DRAFT
  // case note (founder ruling 2026-07-06). Case notes are the only road onto
  // an invoice — this creates an inert draft the CM posts from the record;
  // nothing bills or seeds the record until they do. Server idempotency on
  // content identity means a double-click returns the same draft.
  // The report text and its hours are assembled at the press and travel
  // as the variables (fileVarsOf).
  const fileToRecordMutation = useMutation({
    mutationFn: (vars: { clientId: string; firstName: string; body: string | null; hours: number | null }) => {
      if (vars.body === null) {
        throw new Error('file-to-record called without a Care Management draft');
      }
      return noteDraftsApi.create(tenantSlug, vars.clientId, {
        kind: 'assessment',
        body: vars.body,
        // A draft's date defaults to today; the care manager confirms it
        // (and the hours) when they post it from the record.
        occurred_on: noteDateDefault(),
        rate_band: null,
        // The editable billing suggestion rides along as the draft's hours;
        // it is a suggestion, adjusted at post time (never billed here).
        hours: vars.hours,
        after_hours: false,
        source: 'assessment_drafter',
      });
    },
    onSuccess: (res, vars) => {
      setFileError(null);
      setFileResult({ firstName: vars.firstName, alreadyFiled: res.already_filed });
    },
    onError: (e: unknown) => {
      setFileResult(null);
      setFileError(assessmentFileKeyOf(e));
    },
  });

  if (!canUseAiTools(whoami)) {
    return (
      <main className="max-w-6xl mx-auto px-4 py-6">
        <EmptyAiToolsTile />
      </main>
    );
  }

  // Filing is enabled only for a real, lookup-picked client whose name still
  // matches what's on screen (a manual name edit after the pick unbinds it),
  // for a Care Management assessment, by a CM who can write clinical notes.
  const fileableClient = bound;
  // The door is live for every seat that can author clinical notes: the
  // office and supervisors, line care managers and SPICE therapists
  // (primary_cm, no view_workspace), and a demo-only author (board_observer,
  // write_demo_clinical): filing routes through createDraft, which the repo
  // fences to is_demo clients server-side for that seat, and to the
  // author's own care team for everyone else.
  const canFileToRecord = canAuthorClinicalNotes(whoami);

  const handleDraftReady = (
    response: AssessmentDraftResponse | SpiceAssessmentDraftResponse,
    formInput: AssessmentFormInputSnapshot,
    mode: AssessmentMode,
  ): void => {
    setDraftResponse(response);
    setLastFormInput(formInput);
    setLastMode(mode);
    // The prose is written with the choice this generation recorded.
    setProseChoice(formInput.pronouns);
    setReopenedWithoutPronouns(false);
    setSaveError(null);
    setSavedDraftId(null);
    // A fresh draft starts with an empty Recommendations set.
    setRecommendedResources([]);
    setRecPickerOpen(false);
    // A fresh generation resets the refine trail + the trust states.
    setPreviousResponse(null);
    setRefineFailure(null);
    setGapFailure(null);
    setParagraphStates({});
    setLastRunSummary(null);
    setFileResult(null);
    setFileError(null);
    // Post-generation act: the input collapses to the summary chip.
    setInputExpanded(false);
  };

  const isSpice = mode === 'spice';
  const cmResponse =
    draftResponse !== null && lastMode === 'care_management'
      ? (draftResponse as AssessmentDraftResponse)
      : null;
  const gapCounts = cmResponse !== null ? gapCountsBySection(cmResponse.follow_up_questions) : {};

  // The input form + client block, reused by both acts (centered column
  // pre-generation; the expandable panel post-generation).
  const inputForm = (
    <div className="space-y-6">
      <AssessmentClientBlock
        tenantSlug={tenantSlug}
        canLookup={canViewAnyClientRoster(whoami)}
        caseload={ownCaseload}
        firstName={clientFirstName}
        lastName={clientLastName}
        recipient={recipient}
        pronounPreset={pronounPreset}
        pronounCustom={pronounCustom}
        onFirstNameChange={setClientFirstName}
        onLastNameChange={setClientLastName}
        onRecipientChange={setRecipient}
        onPronounPresetChange={handlePronounPresetChange}
        onPronounCustomChange={handlePronounCustomChange}
        onClientPicked={setPickedClient}
        disabled={isGenerating}
        spice={isSpice}
      />
      <section className={CARD_CLS}>
        <span className={`${EYEBROW_CLS} text-sage-400`}>Your notes</span>
        <div className="mt-4">
          <AssessmentDraftForm
            key={`${reopenedDraftId ?? 'new'}:${formGeneration}`}
            keptFields={keptFields}
            onFieldsChange={setFormFields}
            tenantSlug={tenantSlug}
            mode={mode}
            onModeChange={setMode}
            modeFixed={spiceOnlyViewer}
            recipient={recipient}
            clientFirstName={clientFirstName}
            clientLastName={clientLastName}
            pronouns={pronounChoice}
            pronounsComplete={pronounsComplete}
            pronounsRequired={pronounsRequired}
            picksPending={picksPending}
            onGeneratingChange={setIsGenerating}
            onDraftReady={handleDraftReady}
            hasDraft={draftResponse !== null}
            resumed={
              reopenedDraftId !== null && lastFormInput !== null
                ? { draftId: reopenedDraftId, formInput: lastFormInput }
                : undefined
            }
          />
        </div>
      </section>
    </div>
  );

  // ---- Act 1: pre-generation (one centered column; no library, no
  // finalize apparatus). ----
  if (draftResponse === null) {
    // Reopening a saved report: say so while it loads; a deleted draft
    // says so and points back to the inbox; any other failure names itself
    // above the form.
    const resumeMissing =
      resumeId !== null && resumeError && resumeErr instanceof ApiError && resumeErr.status === 404;
    const resumeFailed = resumeId !== null && resumeError && !resumeMissing;
    const resumeOpening = resumeId !== null && resumeLoading;
    return (
      <main
        className="mx-auto max-w-[1160px] px-5 pt-8 pb-20 md:px-10"
        data-testid="tool-shell-assessment"
      >
        <ToolPageHeader tool={TOOL} tenantSlug={tenantSlug} spice={isSpice} />
        <div className="mx-auto mt-5 max-w-[720px]" data-testid="assessment-stage-compose">
          {kept.restored && <KeptWorkNotice onDiscard={kept.discard} />}
          {resumeMissing ? (
            <p
              className="rounded-card bg-sand px-4 py-6 text-center text-sm text-ink-soft shadow-sh1"
              data-testid="assessment-draft-missing"
            >
              This draft is no longer available.{' '}
              <Link to={`/t/${tenantSlug}/ai-tools/drafts`} className="underline">
                Back to drafts
              </Link>
            </p>
          ) : resumeOpening ? (
            <section className={CARD_CLS} data-testid="assessment-draft-opening">
              <p className="text-center text-sm font-medium text-sage-400">Opening your saved report…</p>
            </section>
          ) : (
            <>
              <AssessmentDraftHelp />
              {resumeFailed && (
                <div className="mt-5">
                  <ErrorBanner error={resumeErr} />
                </div>
              )}
              <div className="mt-5">{inputForm}</div>
              {isGenerating && (
                <section className={`${CARD_CLS} mt-6`}>
                  <ToolDraftingState spice={isSpice} />
                </section>
              )}
            </>
          )}
        </div>
      </main>
    );
  }

  // ---- Acts 2 + 3: post-generation review + the finalize ladder. ----
  const notesWords = wordCount(lastFormInput?.notes ?? '');

  const draftBody: ReactNode =
    lastMode === 'spice' ? (
      <SpiceAssessmentOutput response={draftResponse as SpiceAssessmentDraftResponse} />
    ) : (
      <AssessmentDraftOutput
        response={cmResponse!}
        recommendedResources={recommendedResources}
        onRemoveRecommendation={handleRemoveRecommendation}
        currentPronounKey={currentPronounKey}
        onRetryRecommendation={retryRecommendation}
        onResponseChange={handleResponseChange}
        paragraphStates={paragraphStates}
        onParagraphEdited={handleParagraphEdited}
        onParagraphSeen={handleParagraphSeen}
        gapCounts={gapCounts}
        hideBilling
        followUpsSlot={
          <AssessmentGapInterview
            questions={cmResponse!.follow_up_questions}
            onFillIn={handleFillIn}
            pending={refineMutation.isPending}
            error={gapFailure === null ? null : refineErrorText(gapFailure)}
          />
        }
        addRecommendationSlot={
          <AssessmentRecommendationInsert
            open={recPickerOpen}
            onOpenChange={setRecPickerOpen}
            tenantSlug={tenantSlug}
            clientFirstName={clientFirstName}
            pronouns={pronounChoice}
            pronounsNeeded={libraryNeedsPronouns}
            pronounPromptText={LIBRARY_PRONOUN_PROMPT}
            existingTopics={[
              ...cmResponse!.sections.summary_and_recommendations.recommendations.map(
                (rec) => rec.label,
              ),
              ...recommendedResources.map((rec) => rec.topic),
            ]}
            onInsert={handleInsertRecommendation}
            disabled={refineMutation.isPending}
          />
        }
      />
    );

  return (
    <main
      className="mx-auto max-w-[1160px] px-5 pt-8 pb-20 md:px-10"
      data-testid="tool-shell-assessment"
    >
      <ToolPageHeader tool={TOOL} tenantSlug={tenantSlug} spice={isSpice} />
      {kept.restored && (
        <div className="mt-5">
          <KeptWorkNotice onDiscard={kept.discard} />
        </div>
      )}
      <div className="mt-5">
        <AssessmentDraftHelp />
      </div>

      <div className="mt-[18px]" data-testid="assessment-stage-review">
        {/* The collapsed input: a summary chip; View / edit re-expands. */}
        <div
          className="flex flex-wrap items-center gap-3 rounded-pill border border-sage-800/[8%] bg-paper px-4 py-2 shadow-sh1"
          data-testid="assessment-input-chip"
        >
          <span className="text-sm text-ink">
            <span className="font-semibold">Your notes</span>
            {' · '}
            {notesWords.toLocaleString()} {notesWords === 1 ? 'word' : 'words'}
          </span>
          {isGenerating && <span className="text-xs italic text-sage-400">Regenerating…</span>}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setInputExpanded((v) => !v)}
            aria-expanded={inputExpanded}
            data-testid="assessment-input-chip-toggle"
          >
            {inputExpanded ? 'Collapse' : 'View / edit'}
          </Button>
        </div>
        {inputExpanded && (
          <div className="mt-4" data-testid="assessment-input-expanded">
            {inputForm}
          </div>
        )}

        {/* The draft takes the stage at full width, with the sticky
            section outline beside it (CM only; SPICE has its own shape). */}
        <div
          className={
            'mt-6 ' +
            (lastMode === 'care_management'
              ? 'lg:grid lg:grid-cols-[210px_minmax(0,1fr)] lg:gap-8'
              : '')
          }
        >
          {lastMode === 'care_management' && (
            <AssessmentSectionOutline
              gapCounts={gapCounts}
              onAddRecommendation={() => {
                // Opens the SAME picker the inline affordance owns, then
                // brings it into view (founder polish, item 4a).
                setRecPickerOpen(true);
                document
                  .getElementById('assessment-recommendation-insert')
                  ?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
              }}
            />
          )}
          <div className="min-w-0 space-y-4">
            {pronounMismatch && proseChoice !== undefined && (
              <p
                className="rounded-[12px] bg-sand px-3 py-2 text-sm text-sage-800"
                data-testid="assessment-pronoun-mismatch"
                aria-live="polite"
              >
                {lastMode === 'spice'
                  ? `The report was written with ${pronounChoiceLabel(proseChoice)}. To update it, open View / edit and use "Generate SPICE report".`
                  : `The report was written with ${pronounChoiceLabel(proseChoice)}. Under "Want changes?", ask for the new pronouns, then use "Make changes and run again".`}
              </p>
            )}
            {lastRunSummary !== null && (
              <p
                className="rounded-[12px] bg-sand px-3 py-2 text-sm text-sage-800"
                data-testid="assessment-run-summary"
                aria-live="polite"
              >
                {lastRunSummary}
              </p>
            )}
            {draftBody}

            {/* Second-round edits: say what to change and run it again.
                Care Management only. */}
            {lastMode === 'care_management' && (
              <RefinePanel
                onRefine={handleRefine}
                pending={refineMutation.isPending}
                error={refineFailure === null ? null : refineErrorText(refineFailure)}
                onRestore={previousResponse !== null ? handleRestorePrevious : undefined}
                maxChars={emailMaxChars}
                testIdPrefix="assessment-refine"
              />
            )}

            {/* ---- Act 3: the finalize ladder. ---- */}
            <section className="space-y-3 pt-2" data-testid="assessment-finalize-ladder">
              {cmResponse !== null && (
                <AssessmentBillingField
                  key={cmResponse.ai_invocation_id}
                  suggestion={cmResponse.billing_suggestion}
                  onChange={handleBillingChange}
                  disabled={refineMutation.isPending || saveMutation.isPending}
                />
              )}
              <HelperActionRows
                testId="assessment-save-row"
                primary={
                  savedDraftId !== null ? (
                    <>
                      <DownloadDocxButton
                        url={aiToolsApi.assessmentDownloadUrl(tenantSlug, savedDraftId)}
                        filename="assessment.docx"
                        variant="primary"
                        testId="assessment-download-link"
                      >
                        Download Word
                      </DownloadDocxButton>
                      <DocumentPrintPdfButton
                        url={aiToolsApi.assessmentDownloadUrl(tenantSlug, savedDraftId)}
                        filename="assessment.docx"
                        testId="assessment-report-print-pdf"
                      />
                      <SaveToClientDocumentsButton
                        tenantSlug={tenantSlug}
                        draftId={savedDraftId}
                        clientId={savedClientId}
                        testId="assessment-save-to-documents"
                      />
                    </>
                  ) : (
                    <>
                      <Button
                        type="button"
                        disabled
                        variant="primary"
                        data-testid="assessment-download-disabled"
                      >
                        Download Word
                      </Button>
                      <Button type="button" variant="secondary" disabled data-testid="assessment-report-print-pdf-disabled">
                        {PRINT_PDF_LABEL}
                      </Button>
                    </>
                  )
                }
                secondary={
                  <Button
                    type="button"
                    onClick={() => saveMutation.mutate(saveVarsOf())}
                    disabled={
                      saveMutation.isPending ||
                      savedDraftId !== null ||
                      !pronounsComplete ||
                      picksPending
                    }
                    variant="secondary"
                    aria-label="Save draft"
                  >
                    {savedDraftId !== null
                      ? '✓ Saved'
                      : saveMutation.isPending
                        ? 'Saving…'
                        : 'Save draft'}
                  </Button>
                }
                quiet={
                  cmResponse !== null && canFileToRecord ? (
                    <Button
                      type="button"
                      onClick={() =>
                        fileableClient !== null &&
                        fileToRecordMutation.mutate({
                          clientId: fileableClient.id,
                          firstName: fileableClient.firstName,
                          ...fileVarsOf(),
                        })
                      }
                      disabled={fileableClient === null || fileToRecordMutation.isPending}
                      variant="ghost"
                      size="sm"
                      data-testid="assessment-file-to-record-button"
                    >
                      {fileToRecordMutation.isPending
                        ? 'Filing…'
                        : fileableClient !== null
                          ? `File to ${fileableClient.firstName}'s record as a draft case note`
                          : 'File to the record as a draft case note'}
                    </Button>
                  ) : undefined
                }
              />
              <p className="text-xs text-ink-soft" data-testid="assessment-finalize-copy">
                {savedDraftId !== null ? (
                  <>
                    Saved to your drafts inbox under Writing.{' '}
                    <Link
                      to={`/t/${tenantSlug}/ai-tools/drafts`}
                      className="text-sage-700 underline hover:text-sage-900"
                      data-testid="assessment-saved-link"
                    >
                      Open the drafts inbox
                    </Link>
                  </>
                ) : (
                  'Saving files this draft to your drafts inbox under Writing and turns on the Word download.'
                )}
              </p>
              {saveError !== null && (
                <ErrorNotice
                  surface="assessment_draft_save"
                  code={saveError.key}
                  vars={saveError.vars}
                  data-testid="assessment-save-error"
                />
              )}

              {/* File to the record as a DRAFT case note: the assessment's
                  road onto the record and the invoice runs through a case
                  note. CM mode + any seat that authors clinical notes (see
                  canFileToRecord above). */}
              {cmResponse !== null && canFileToRecord && (
                <div
                  className="mt-1 rounded-card border border-sage-800/[12%] bg-sand/50 p-4"
                  data-testid="assessment-file-to-record"
                >
                  <p className="text-sm font-semibold text-sage-800">
                    Add it to the client's record
                  </p>
                  <p className="mt-1 text-xs text-ink-soft">
                    File this assessment to the record as a draft case note. You post it from the
                    record when it's final. That's what puts it on the record and the invoice.
                  </p>

                  {fileableClient === null && (
                    <p
                      className="mt-2 text-xs text-ink-soft"
                      data-testid="assessment-file-needs-client"
                    >
                      Pick this client from the record in the Client block above to file the
                      assessment to their record.
                    </p>
                  )}
                  {fileResult !== null && (
                    <p
                      className="mt-2 text-sm text-sage-800"
                      data-testid="assessment-file-confirm"
                      aria-live="polite"
                    >
                      {fileResult.alreadyFiled
                        ? `This assessment is already on ${fileResult.firstName}'s record as a draft case note. Post it from their record when it's final.`
                        : `Filed to ${fileResult.firstName}'s record as a draft case note. Post it from their record when it's final.`}
                    </p>
                  )}
                  {fileError !== null && (
                    <div className="mt-2">
                      <ErrorNotice
                        surface="assessment_file"
                        code={fileError.key}
                        vars={fileError.vars}
                        data-testid="assessment-file-error"
                      />
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </main>
  );
}
